import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";

import { QuillModule } from "ngx-quill";
import Quill from "quill";

const Delta = Quill.import("delta");

// Fix Quill 2.0 converting spaces to &nbsp; by normalizing on paste
const nbspClipboardMatcher: any = (node: HTMLElement, delta: any) => {
  const ops = delta.ops.map((op: any) => {
    if (typeof op.insert === "string") {
      return { ...op, insert: op.insert.replace(/\u00A0/g, " ") };
    }
    return op;
  });
  return new Delta(ops);
};

import { AppMatModule } from "./app-mat.module";
import { NotFoundComponent } from "./components/not-found/not-found.component";
import { GlobalErrorModalComponent } from "./components/global-error-modal/global-error-modal.component";
import { ScrollToTopComponent } from "./components/scroll-to-top/scroll-to-top.component";
import { PageHeaderComponent } from "./components/page-header/page-header.component";
import { ComingSoonComponent } from "./components/coming-soon/coming-soon.component";
import { InlineEditComponent } from "./components/inline-edit/inline-edit.component";
import { HelpModalComponent } from "./modals/help-modal/help-modal.component";
import { PageValidationModule } from "./components/page-validation/page-validation.module";
import { DocumentUploadModule } from "./components/document-upload/document-upload.module";
import { DateWithTimePipe } from "./pipes/local-date-time.pipe";
import { EnumToStringPipe } from "./pipes/enum-to-string.pipe";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { EnumToArrayPipe } from "./pipes/enum-to-array.pipe";
import { DatetimePickerModule } from "./components/datetime-picker/datetime-picker.module";
import { EllipsisPipe } from "./pipes/ellipsis.pipe";
import { ShortDatePipe } from "./pipes/short-date.pipe";
import { PhonePipe } from "./pipes/phone.pipe";
import { FormErrorComponent } from "./components/form-error/form-error.component";
import { VarDirective } from "./directives/ng-var.directive";
import { SignalrRoutingHubComponent } from "./components/signalr-routing-hub/signalr-routing-hub.component";
import { JoinPipe } from "./pipes/join.pipe";
import { TextBlockComponent } from "./components/text-block/text-block.component";
import { EmailModalComponent } from "./components/email-modal/email-modal.component";
import { FilterPipe } from "./pipes/filter.pipe";
import { TagPickerTabsComponent } from "./components/tag-picker-tabs/tag-picker-tabs.component";
import { ChatModalComponent } from "./components/chat-modal/chat-modal.component";
import { RenameModalComponent } from "./components/rename-modal/rename-modal.component";
import { MtxColorpickerModule } from "@ng-matero/extensions/colorpicker";
import { ProgressBarComponent } from "./components/progress-bar/progress-bar.component";
import { UtcShortDatePipe } from "./pipes/utc-short-date.pipe";
import { SafeStyleHtmlPipe } from "./pipes/safe-style-html.pipe";
import { TargetComparisonOperatorPipe } from "./pipes/target-comparison-operator.pipe";
import { OrderByPipe } from "./pipes/order-by.pipe";
import { WordWrapPipe } from "./pipes/word-wrap.pipe";
import { UserRolesSelectionComponent } from "./components/user-roles-selection/user-roles-selection.component";
import { EmailPreviewModalComponent } from "./components/email-preview-modal/email-preview-modal.component";
import { LayoutModule } from "@progress/kendo-angular-layout";
import { NoPrintDirective } from "./directives/no-print.directive";
import { EmptyComponent } from "./components/empty/empty.component";
import { AddressInputComponent } from "./components/dispute-resolution/address-input/address-input.component";
import { CopyFieldsCheckboxComponent } from "./components/dispute-resolution/copy-fields-checkbox/copy-fields-checkbox.component";
import { ThankYouCreateAccountComponent } from "./components/dispute-resolution/thank-you-create-account/thank-you-create-account.component";
import { ThankYouVerifyEmailComponent } from "./components/dispute-resolution/thank-you-verify-email/thank-you-verify-email.component";
import { ThankYouSubmissionComponent } from "./components/dispute-resolution/thank-you-submission/thank-you-submission.component";
import { AddEditPartyInformationComponent } from "./components/dispute-resolution/add-edit-party-information/add-edit-party-information.component";
import { PartiesListComponent } from "./components/dispute-resolution/parties-list/parties-list.component";
import { DocumentsListComponent } from "./components/dispute-resolution/documents-list/documents-list.component";
import { TagPickerModalComponent } from "../documentation/tag-picker-modal/tag-picker-modal.component";
import { StatusChangesComponent } from "./components/dispute-resolution/status-changes/status-changes.component";
import { LinkedCasesComponent } from "./components/dispute-resolution/linked-cases/linked-cases.component";
import { DisputeExportButtonComponent } from "./components/dispute-resolution/dispute-export-button/dispute-export-button.component";
import { IssuesListComponent } from "./components/dispute-resolution/issues-list/issues-list.component";
import { AddEditIssueComponent } from "./components/dispute-resolution/add-edit-issue/add-edit-issue.component";
import { MeetingsListComponent } from "./components/dispute-resolution/meetings-list/meetings-list.component";
import { HearingsListComponent } from "./components/dispute-resolution/hearings-list/hearings-list.component";
import { AddEditMeetingInformationComponent } from "./components/dispute-resolution/add-edit-meeting-information/add-edit-meeting-information.component";
import { CallLogsListComponent } from "./components/dispute-resolution/call-logs-list/call-logs-list.component";
import { AddEditCallLogComponent } from "./components/dispute-resolution/add-edit-call-log/add-edit-call-log.component";
import { PublicViewDocumentsListComponent } from "./components/dispute-resolution/public-view-documents-list/public-view-documents-list.component";
import { PublicViewAdministrativeUnitsListComponent } from "./components/dispute-resolution/public-view-administrative-units-list/public-view-administrative-units-list.component";
import { FileUploadComponent } from "./components/dispute-resolution/file-upload/file-upload.component";
import { UploadProgressSnackbarComponent } from "./components/upload-progress-snackbar/upload-progress-snackbar.component";

@NgModule({
  declarations: [
    NotFoundComponent,
    GlobalErrorModalComponent,
    ScrollToTopComponent,
    PageHeaderComponent,
    ComingSoonComponent,
    InlineEditComponent,
    HelpModalComponent,
    DateWithTimePipe,
    EnumToStringPipe,
    EnumToArrayPipe,
    EllipsisPipe,
    ShortDatePipe,
    UtcShortDatePipe,
    FormErrorComponent,
    PhonePipe,
    VarDirective,
    NoPrintDirective,
    SignalrRoutingHubComponent,
    JoinPipe,
    SafeStyleHtmlPipe,
    TextBlockComponent,
    EmailModalComponent,
    FilterPipe,
    TagPickerTabsComponent,
    ChatModalComponent,
    RenameModalComponent,
    ProgressBarComponent,
    TargetComparisonOperatorPipe,
    OrderByPipe,
    WordWrapPipe,
    UserRolesSelectionComponent,
    EmailPreviewModalComponent,
    EmptyComponent,
    AddressInputComponent,
    CopyFieldsCheckboxComponent,
    ThankYouCreateAccountComponent,
    ThankYouVerifyEmailComponent,
    ThankYouSubmissionComponent,
    AddEditPartyInformationComponent,
    PartiesListComponent,
    DocumentsListComponent,
    PublicViewDocumentsListComponent,
    PublicViewAdministrativeUnitsListComponent,
    IssuesListComponent,
    AddEditIssueComponent,
    MeetingsListComponent,
    HearingsListComponent,
    AddEditMeetingInformationComponent,
    CallLogsListComponent,
    AddEditCallLogComponent,
    TagPickerModalComponent,
    StatusChangesComponent,
    LinkedCasesComponent,
    DisputeExportButtonComponent,
    FileUploadComponent,
    UploadProgressSnackbarComponent,
  ],
  imports: [
    CommonModule,
    AppMatModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    QuillModule.forRoot({
      modules: {
        toolbar: [
          ["bold", "italic", "underline", "strike"], // toggled buttons
          ["blockquote", "code-block"],

          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: "ordered" }, { list: "bullet" }],
          [{ script: "sub" }, { script: "super" }], // superscript/subscript
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
          [{ direction: "rtl" }], // text direction

          [{ size: ["small", false, "large", "huge"] }], // custom dropdown
          [{ header: [1, 2, 3, 4, 5, 6, false] }],

          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ font: [] }],
          [{ align: [] }],

          ["clean"], // remove formatting button
        ],
        clipboard: {
          matchers: [
            [Node.ELEMENT_NODE, nbspClipboardMatcher],
            [Node.TEXT_NODE, nbspClipboardMatcher],
          ],
        },
      },
    }),
    PageValidationModule,
    DocumentUploadModule,
    NgxMaterialTimepickerModule,
    DatetimePickerModule,
    MtxColorpickerModule,
    LayoutModule,
  ],
  exports: [
    GlobalErrorModalComponent,
    ScrollToTopComponent,
    PageHeaderComponent,
    ComingSoonComponent,
    InlineEditComponent,
    HelpModalComponent,
    AppMatModule,
    FormErrorComponent,
    PageValidationModule,
    DateWithTimePipe,
    EnumToStringPipe,
    EnumToArrayPipe,
    DatetimePickerModule,
    EllipsisPipe,
    ShortDatePipe,
    UtcShortDatePipe,
    PhonePipe,
    VarDirective,
    NoPrintDirective,
    SignalrRoutingHubComponent,
    JoinPipe,
    SafeStyleHtmlPipe,
    TextBlockComponent,
    FilterPipe,
    TagPickerTabsComponent,
    MtxColorpickerModule,
    ProgressBarComponent,
    TargetComparisonOperatorPipe,
    OrderByPipe,
    WordWrapPipe,
    UserRolesSelectionComponent,
    EmailPreviewModalComponent,
    AddressInputComponent,
    CopyFieldsCheckboxComponent,
    ThankYouCreateAccountComponent,
    ThankYouVerifyEmailComponent,
    AddEditPartyInformationComponent,
    PartiesListComponent,
    DocumentsListComponent,
    PublicViewDocumentsListComponent,
    PublicViewAdministrativeUnitsListComponent,
    IssuesListComponent,
    AddEditIssueComponent,
    MeetingsListComponent,
    HearingsListComponent,
    AddEditMeetingInformationComponent,
    CallLogsListComponent,
    AddEditCallLogComponent,
    TagPickerModalComponent,
    StatusChangesComponent,
    LinkedCasesComponent,
    DisputeExportButtonComponent,
    FileUploadComponent,
  ],
})
export class SharedModule { }
